/**
 * MFAMethodList Component
 *
 * Displays user's MFA method types (one card per type) with registered instances shown within each card.
 *
 * Features:
 * - Display ALL supported MFA method types (registered and unregistered)
 * - One card per METHOD TYPE (not per device)
 * - Show registered instances within each card
 * - Security strength indicators with visual bars (1-4 level)
 * - Large icons (48x48px) for each authentication method type
 * - Professional card layout matching ID.me design
 * - "Set Up" button for unregistered methods, "Add" button for registered methods
 * - Remove individual instances
 * - Error handling with retry functionality
 * - Loading states with spinner
 * - Success messages with auto-dismiss
 *
 * Optimistic Updates (Azure AD Eventual Consistency):
 * - Add operations: Email appears in UI immediately, remains visible after API success
 * - Delete operations: Email disappears from UI immediately, syncs with Azure AD in background (30-60s)
 * - Sync indicator: Shows "Syncing with Azure AD..." for 5 seconds (represents Azure AD propagation time)
 * - No refresh needed: Added emails stay visible (correctly reflects successful Azure AD enrollment)
 * - Eventual consistency: Azure AD takes 30-60s to propagate, but user sees immediate feedback
 * - Error recovery: Rolls back optimistic changes on failure, shows error toast with retry option
 * - Re-open modal: On add failure, modal re-opens automatically for retry
 */

import { useState, useEffect } from 'react';
import {
  Button,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  ShieldKeyholeRegular,
  PhoneRegular,
  PhoneDesktopRegular,
  MailRegular,
  WindowMultipleRegular,
  AddRegular,
} from '@fluentui/react-icons';
import { SelfServiceApiService } from '../../services/selfServiceApi';
import { Alert } from '../common/Alert';
import { SecurityStrengthIndicator } from './SecurityStrengthIndicator';
import { EmailEnrollmentModal, PhoneEnrollmentModal, AuthenticatorInstructionsModal, PasskeyInstructionsModal } from './modals';
import type { AuthenticationMethod } from '../../types/selfService';

// Define all supported method types ordered by security level (highest to lowest)
// Matches the sorting in Tenant Security > Authentication Methods tab
const SUPPORTED_METHOD_TYPES = [
  // Level 3 (High security)
  'fido2AuthenticationMethod',
  'microsoftAuthenticatorAuthenticationMethod',
  'windowsHelloForBusinessAuthenticationMethod',
  // Level 1 (Low security)
  'phoneAuthenticationMethod',
  'emailAuthenticationMethod',
];

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  methodCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '12px',
    borderTopWidth: '2px',
    borderRightWidth: '2px',
    borderBottomWidth: '2px',
    borderLeftWidth: '2px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    transitionProperty: 'border-color',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease',
    ':hover': {
      borderTopColor: tokens.colorNeutralStroke1Hover,
      borderRightColor: tokens.colorNeutralStroke1Hover,
      borderBottomColor: tokens.colorNeutralStroke1Hover,
      borderLeftColor: tokens.colorNeutralStroke1Hover,
    },
  },
  methodHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalL,
  },
  methodIcon: {
    flexShrink: 0,
    color: tokens.colorBrandForeground1,
    fontSize: '48px',
    width: '48px',
    height: '48px',
  },
  methodInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    flex: 1,
  },
  methodTitleSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  methodType: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
  },
  methodDescription: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  actionButton: {
    alignSelf: 'flex-start',
  },
  instanceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalM,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
  },
  instanceItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
  },
  instanceDetails: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  instanceActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacingVerticalXXXL,
  },
  syncIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXS,
  },
});

/**
 * Get user-friendly label for authentication method type
 */
function getMethodTypeLabel(type: string): string {
  const typeMap: Record<string, string> = {
    phoneAuthenticationMethod: 'Text Message or Phone Call',
    fido2AuthenticationMethod: 'Physical Security Key (FIDO2)',
    microsoftAuthenticatorAuthenticationMethod: 'Authenticator App',
    emailAuthenticationMethod: 'Email',
    windowsHelloForBusinessAuthenticationMethod: 'Windows Hello',
  };

  return typeMap[type] || type;
}

/**
 * Get description text for authentication method type
 */
function getMethodDescription(type: string): string {
  const descriptionMap: Record<string, string> = {
    phoneAuthenticationMethod: 'Verification codes are sent by text message or phone call.',
    fido2AuthenticationMethod:
      'Sign in using a physical hardware security key (YubiKey, Google Titan Key, etc.). Note: Password manager passkeys are not supported.',
    microsoftAuthenticatorAuthenticationMethod:
      'Approve sign-ins via Push Notifications sent to the Microsoft Authenticator mobile app.',
    emailAuthenticationMethod: 'Receive verification codes via email.',
    windowsHelloForBusinessAuthenticationMethod:
      'Sign in with Windows Hello biometrics.',
  };

  return descriptionMap[type] || '';
}

/**
 * Get security level (1-3) and text for authentication method type based on Microsoft Entra official ratings
 * Reference: https://learn.microsoft.com/en-us/entra/identity/authentication/how-to-authentication-methods-manage
 */
function getSecurityLevel(
  type: string
): { level: number; securityText: string } {
  const securityMap: Record<string, { level: number; securityText: string }> =
    {
      // Level 3: High security
      fido2AuthenticationMethod: {
        level: 3,
        securityText: 'High security',
      },
      windowsHelloForBusinessAuthenticationMethod: {
        level: 3,
        securityText: 'High security',
      },
      microsoftAuthenticatorAuthenticationMethod: {
        level: 3,
        securityText: 'High security',
      },
      // Level 1: Low security
      phoneAuthenticationMethod: { level: 1, securityText: 'Low security' },
      emailAuthenticationMethod: { level: 1, securityText: 'Low security' },
    };

  return (
    securityMap[type] || { level: 2, securityText: 'Medium security' }
  );
}

/**
 * Get icon component for authentication method type
 */
function getMethodIcon(type: string) {
  const iconMap: Record<string, JSX.Element> = {
    phoneAuthenticationMethod: <PhoneRegular />,
    fido2AuthenticationMethod: <ShieldKeyholeRegular />,
    microsoftAuthenticatorAuthenticationMethod: <PhoneDesktopRegular />,
    emailAuthenticationMethod: <MailRegular />,
    windowsHelloForBusinessAuthenticationMethod: <WindowMultipleRegular />,
  };

  return iconMap[type] || <ShieldKeyholeRegular />;
}

/**
 * Get display details for a specific authentication method instance
 */
function getInstanceDetails(method: AuthenticationMethod): string {
  const details: string[] = [];

  if (method.displayName) {
    details.push(method.displayName);
  }

  if (method.phoneNumber) {
    details.push(method.phoneNumber);
  }

  if (method.email) {
    details.push(method.email);
  }

  if (method.createdAt) {
    const date = new Date(method.createdAt);
    details.push(`Added ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`);
  }

  return details.join(' ');
}

export function MFAMethodList() {
  const styles = useStyles();

  const [methods, setMethods] = useState<AuthenticationMethod[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [removingMethodId, setRemovingMethodId] = useState<string | null>(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [isAuthenticatorModalOpen, setIsAuthenticatorModalOpen] = useState(false);
  const [isPasskeyModalOpen, setIsPasskeyModalOpen] = useState(false);
  const [syncingMethodIds, setSyncingMethodIds] = useState<Set<string>>(new Set());

  // Group methods by type
  const methodsByType = methods.reduce((acc, method) => {
    if (!acc[method.type]) {
      acc[method.type] = [];
    }
    acc[method.type].push(method);
    return acc;
  }, {} as Record<string, AuthenticationMethod[]>);

  // Fetch MFA methods
  const fetchMethods = async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const response = await SelfServiceApiService.listMFAMethods();
      setMethods(response.methods);
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Failed to load authentication methods. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Load methods on mount
  useEffect(() => {
    fetchMethods();
  }, []);

  // Auto-dismiss success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Handle method removal with optimistic update
  const handleRemoveMethod = async (methodId: string) => {
    setRemovingMethodId(methodId);
    setErrorMessage(null);
    setSuccessMessage(null);

    // OPTIMISTIC UPDATE: Remove from UI immediately
    const methodToRemove = methods.find(m => m.id === methodId);
    if (!methodToRemove) return;

    const previousMethods = [...methods];
    setMethods(methods.filter(m => m.id !== methodId));

    // Add to syncing set and show indicator
    setSyncingMethodIds(prev => new Set(prev).add(methodId));

    // Auto-hide sync indicator after 5 seconds (Azure AD eventual consistency)
    const syncTimeout = setTimeout(() => {
      setSyncingMethodIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(methodId);
        return newSet;
      });
    }, 5000);

    try {
      // Pass method type to avoid eventual consistency issues with newly created methods
      const response = await SelfServiceApiService.removeMFAMethod(methodId, methodToRemove.type);

      if (response.success) {
        setSuccessMessage(response.message);
        // Clear sync indicator immediately on success
        clearTimeout(syncTimeout);
        setSyncingMethodIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(methodId);
          return newSet;
        });
      } else {
        // ROLLBACK: Restore method on failure
        setMethods(previousMethods);
        clearTimeout(syncTimeout);
        setSyncingMethodIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(methodId);
          return newSet;
        });
        setErrorMessage('Failed to remove authentication method. Please try again.');
      }
    } catch (error) {
      // ROLLBACK: Restore method on error
      setMethods(previousMethods);
      clearTimeout(syncTimeout);
      setSyncingMethodIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(methodId);
        return newSet;
      });
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Failed to remove authentication method. Please try again.'
      );
    } finally {
      setRemovingMethodId(null);
    }
  };

  // Handle email enrollment with optimistic update
  const handleEmailEnrollment = async (emailAddress: string) => {
    setErrorMessage(null);
    setSuccessMessage(null);

    // OPTIMISTIC UPDATE: Add email method to UI immediately
    const optimisticEmailMethod: AuthenticationMethod = {
      id: '3ddfcfc8-9383-446f-83cc-3ab9be4be18f', // Well-known email method ID
      type: 'emailAuthenticationMethod',
      displayName: null,
      phoneNumber: null,
      email: emailAddress,
      createdAt: new Date().toISOString(),
    };

    const previousMethods = [...methods];
    setMethods([...methods, optimisticEmailMethod]);
    setIsEmailModalOpen(false);

    // Add to syncing set and show indicator
    setSyncingMethodIds(prev => new Set(prev).add(optimisticEmailMethod.id));

    // Auto-hide sync indicator after 5 seconds (Azure AD eventual consistency)
    const syncTimeout = setTimeout(() => {
      setSyncingMethodIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(optimisticEmailMethod.id);
        return newSet;
      });
    }, 5000);

    try {
      const response = await SelfServiceApiService.addEmailMethod(emailAddress);

      if (response.success) {
        setSuccessMessage(response.message);
        // Keep sync indicator showing for 5 seconds (Azure AD eventual consistency)
        // The optimistic email stays visible - it WAS successfully added to Azure AD
        // The sync indicator will auto-hide after 5 seconds to indicate propagation time
        // Don't call fetchMethods() here - Azure AD won't return the new email yet
      } else {
        // ROLLBACK: Remove optimistic method on failure
        setMethods(previousMethods);
        clearTimeout(syncTimeout);
        setSyncingMethodIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(optimisticEmailMethod.id);
          return newSet;
        });
        setErrorMessage('Failed to add email authentication method. Please try again.');
        setIsEmailModalOpen(true); // Re-open modal for retry
      }
    } catch (error) {
      // ROLLBACK: Remove optimistic method on error
      setMethods(previousMethods);
      clearTimeout(syncTimeout);
      setSyncingMethodIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(optimisticEmailMethod.id);
        return newSet;
      });
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Failed to add email authentication method. Please try again.'
      );
      setIsEmailModalOpen(true); // Re-open modal for retry
      // Keep modal open on error so user can retry
      throw error;
    }
  };

  // Handle phone enrollment with optimistic update
  const handlePhoneEnrollment = async (phoneNumber: string, phoneType: string) => {
    setErrorMessage(null);
    setSuccessMessage(null);

    // OPTIMISTIC UPDATE: Add phone method to UI immediately
    const optimisticPhoneMethod: AuthenticationMethod = {
      id: 'temp-' + Date.now(), // Temporary ID
      type: 'phoneAuthenticationMethod',
      displayName: null,
      phoneNumber: phoneNumber,
      email: null,
      createdAt: new Date().toISOString(),
    };

    const previousMethods = [...methods];
    setMethods([...methods, optimisticPhoneMethod]);
    setIsPhoneModalOpen(false);

    // Add to syncing set and show indicator
    setSyncingMethodIds(prev => new Set(prev).add(optimisticPhoneMethod.id));

    // Auto-hide sync indicator after 5 seconds (Azure AD eventual consistency)
    const syncTimeout = setTimeout(() => {
      setSyncingMethodIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(optimisticPhoneMethod.id);
        return newSet;
      });
    }, 5000);

    try {
      const response = await SelfServiceApiService.addPhoneMethod(phoneNumber, phoneType);

      if (response.success && response.methodId) {
        // Clear the original timeout since we're updating the ID
        clearTimeout(syncTimeout);

        // Replace optimistic method with real method from API
        setMethods(prev =>
          prev.map(m =>
            m.id === optimisticPhoneMethod.id
              ? { ...m, id: response.methodId || optimisticPhoneMethod.id }
              : m
          )
        );

        setSuccessMessage(response.message);

        // Auto-dismiss success message after 5 seconds
        setTimeout(() => setSuccessMessage(null), 5000);

        // Update syncing ID to use real method ID
        setSyncingMethodIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(optimisticPhoneMethod.id);
          newSet.add(response.methodId || optimisticPhoneMethod.id);
          return newSet;
        });

        // Set new timeout to clear the real method ID after 5 seconds
        setTimeout(() => {
          setSyncingMethodIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(response.methodId || optimisticPhoneMethod.id);
            return newSet;
          });
        }, 5000);
      } else {
        // ROLLBACK: Remove optimistic method on failure
        setMethods(previousMethods);
        clearTimeout(syncTimeout);
        setSyncingMethodIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(optimisticPhoneMethod.id);
          return newSet;
        });
        setErrorMessage('Failed to add phone authentication method. Please try again.');
        setIsPhoneModalOpen(true); // Re-open modal for retry
      }
    } catch (error) {
      // ROLLBACK: Remove optimistic method on error
      setMethods(previousMethods);
      clearTimeout(syncTimeout);
      setSyncingMethodIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(optimisticPhoneMethod.id);
        return newSet;
      });
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Failed to add phone authentication method. Please try again.'
      );
      setIsPhoneModalOpen(true); // Re-open modal for retry
      throw error;
    }
  };

  // Check if method is implemented (has enrollment flow wired up)
  const isMethodImplemented = (methodType: string): boolean => {
    const implementedMethods = [
      'emailAuthenticationMethod',
      'phoneAuthenticationMethod',
      'microsoftAuthenticatorAuthenticationMethod',
      'fido2AuthenticationMethod',
    ];
    return implementedMethods.includes(methodType);
  };

  // Handle setup button click
  const handleSetupClick = (methodType: string) => {
    if (methodType === 'emailAuthenticationMethod') {
      setIsEmailModalOpen(true);
    } else if (methodType === 'phoneAuthenticationMethod') {
      setIsPhoneModalOpen(true);
    } else if (methodType === 'microsoftAuthenticatorAuthenticationMethod') {
      setIsAuthenticatorModalOpen(true);
    } else if (methodType === 'fido2AuthenticationMethod') {
      setIsPasskeyModalOpen(true);
    }
    // Future: Add handlers for other method types
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spinner size="large" label="Loading authentication methods..." />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {successMessage && (
        <Alert intent="success" data-testid="success-message">
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert intent="error" data-testid="error-message">
          {errorMessage}
          <Button
            appearance="subtle"
            onClick={fetchMethods}
            style={{ marginLeft: tokens.spacingHorizontalM }}
          >
            Try Again
          </Button>
        </Alert>
      )}

      <div className={styles.section}>
        {SUPPORTED_METHOD_TYPES.map((methodType) => {
          const instances = methodsByType[methodType] || [];
          const hasInstances = instances.length > 0;
          const security = getSecurityLevel(methodType);
          const description = getMethodDescription(methodType);

          return (
            <article key={methodType} className={styles.methodCard}>
              <div className={styles.methodHeader}>
                <div className={styles.methodIcon}>{getMethodIcon(methodType)}</div>

                <div className={styles.methodInfo}>
                  <div className={styles.methodTitleSection}>
                    <Text className={styles.methodType}>
                      {getMethodTypeLabel(methodType)}
                    </Text>
                    {description && (
                      <Text className={styles.methodDescription}>
                        {description}
                      </Text>
                    )}
                  </div>

                  <SecurityStrengthIndicator
                    level={security.level}
                    securityText={security.securityText}
                  />
                </div>

                <Button
                  appearance="primary"
                  icon={hasInstances ? <AddRegular /> : undefined}
                  className={styles.actionButton}
                  onClick={() => handleSetupClick(methodType)}
                  disabled={!isMethodImplemented(methodType)}
                  title={!isMethodImplemented(methodType) ? 'Coming soon' : undefined}
                  aria-label={hasInstances ? `Add another ${getMethodTypeLabel(methodType)}` : `Set up ${getMethodTypeLabel(methodType)}`}
                >
                  {hasInstances ? 'Add' : 'Set Up'}
                </Button>
              </div>

              {hasInstances && (
                <div className={styles.instanceList}>
                  {instances.map((instance) => (
                    <div key={instance.id} className={styles.instanceItem}>
                      <div>
                        <Text className={styles.instanceDetails}>
                          {getInstanceDetails(instance)}
                        </Text>
                        {syncingMethodIds.has(instance.id) && (
                          <div className={styles.syncIndicator}>
                            <Spinner size="tiny" />
                            <Text>Syncing with Azure AD...</Text>
                          </div>
                        )}
                      </div>
                      <div className={styles.instanceActions}>
                        <Button
                          appearance="subtle"
                          size="small"
                          aria-label="Rename"
                        >
                          Rename
                        </Button>
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<Dismiss24Regular />}
                          onClick={() => handleRemoveMethod(instance.id)}
                          disabled={removingMethodId === instance.id || syncingMethodIds.has(instance.id)}
                          aria-label={`Remove ${getInstanceDetails(instance)}`}
                        >
                          {removingMethodId === instance.id ? 'Removing...' : 'Remove'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </article>
          );
        })}
      </div>

      <EmailEnrollmentModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        onEnroll={handleEmailEnrollment}
      />

      <PhoneEnrollmentModal
        isOpen={isPhoneModalOpen}
        onClose={() => setIsPhoneModalOpen(false)}
        onEnroll={handlePhoneEnrollment}
      />

      <AuthenticatorInstructionsModal
        isOpen={isAuthenticatorModalOpen}
        onClose={() => setIsAuthenticatorModalOpen(false)}
      />

      <PasskeyInstructionsModal
        isOpen={isPasskeyModalOpen}
        onClose={() => setIsPasskeyModalOpen(false)}
      />
    </div>
  );
}
